import React from 'react';
import { SiteSection, SiteSectionHeader } from '../../components/site';
import type { SolutionPlatformModuleLabel, SolutionPlatformModulesSection } from './types';

function DashboardMockup() {
  return (
    <div className="cl-platform__dashboard">
      <header className="cl-platform__dash-head">
        <span className="cl-platform__dash-dot cl-platform__dash-dot--red" />
        <span className="cl-platform__dash-dot cl-platform__dash-dot--amber" />
        <span className="cl-platform__dash-dot cl-platform__dash-dot--green" />
        <span className="cl-platform__dash-title">Lighting OS Dashboard</span>
        <span className="cl-platform__dash-live" aria-hidden>
          <span className="cl-platform__dash-live-dot" />
          Live
        </span>
      </header>
      <div className="cl-platform__dash-body">
        <div className="cl-platform__dash-row">
          <div className="cl-platform__dash-stat">
            <span className="cl-platform__dash-stat-label">Sites</span>
            <span className="cl-platform__dash-stat-value">24</span>
          </div>
          <div className="cl-platform__dash-stat">
            <span className="cl-platform__dash-stat-label">Active Zones</span>
            <span className="cl-platform__dash-stat-value">128</span>
          </div>
          <div className="cl-platform__dash-stat">
            <span className="cl-platform__dash-stat-label">Faults</span>
            <span className="cl-platform__dash-stat-value cl-platform__dash-stat-value--ok">0</span>
          </div>
        </div>
        <div className="cl-platform__dash-chart">
          <div className="cl-platform__dash-chart-head">
            <span className="cl-platform__dash-chart-label">Energy Consumption</span>
            <span className="cl-platform__dash-chart-badge">-18% vs last week</span>
          </div>
          <svg viewBox="0 0 320 80" className="cl-platform__dash-chart-svg" aria-hidden>
            <defs>
              <linearGradient id="cl-dash-grad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#2563EB" />
                <stop offset="100%" stopColor="#4FD1FF" />
              </linearGradient>
              <linearGradient id="cl-dash-fill" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="rgba(37, 99, 235, 0.35)" />
                <stop offset="100%" stopColor="rgba(37, 99, 235, 0)" />
              </linearGradient>
            </defs>
            <path
              d="M0 60 L40 52 L80 48 L120 38 L160 42 L200 28 L240 32 L280 18 L320 22 L320 80 L0 80 Z"
              fill="url(#cl-dash-fill)"
            />
            <path
              d="M0 60 L40 52 L80 48 L120 38 L160 42 L200 28 L240 32 L280 18 L320 22"
              fill="none"
              stroke="url(#cl-dash-grad)"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <div className="cl-platform__dash-panels">
          <div className="cl-platform__dash-panel">
            <span>Schedule Automation</span>
            <strong>12 scenes active</strong>
          </div>
          <div className="cl-platform__dash-panel">
            <span>Occupancy Map</span>
            <strong>86% zones live</strong>
          </div>
        </div>
      </div>
    </div>
  );
}

export const SolutionCommercialPlatformModules: React.FC<{
  section: SolutionPlatformModulesSection;
}> = ({ section }) => (
  <SiteSection id="platform-modules" className="cl-platform">
    <SiteSectionHeader
      eyebrow={section.eyebrow}
      title={section.title}
      subtitle={section.subtitle}
      align="center"
    />

    <div className="cl-platform__showcase">
      <svg className="cl-platform__connectors" viewBox="0 0 1000 620" aria-hidden>
        <defs>
          <linearGradient id="cl-platform-connector" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#2563EB" stopOpacity="0.15" />
            <stop offset="50%" stopColor="#4FD1FF" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#2563EB" stopOpacity="0.15" />
          </linearGradient>
        </defs>
        {section.modules.map((mod, i) => {
          const pos = getConnectorEnd(mod);
          return (
            <g key={mod.id}>
              <line
                x1={500}
                y1={310}
                x2={pos.x}
                y2={pos.y}
                stroke="url(#cl-platform-connector)"
                strokeWidth="1.5"
                strokeDasharray="5 7"
                className="cl-platform__connector"
                style={{ animationDelay: `${i * 0.2}s` }}
              />
              <circle
                className="cl-platform__connector-node"
                cx={pos.x}
                cy={pos.y}
                r="3"
                style={{ animationDelay: `${i * 0.25}s` }}
              />
            </g>
          );
        })}
      </svg>

      <div className="cl-platform__halo" aria-hidden />

      <div className="cl-platform__hub">
        <DashboardMockup />
      </div>

      <div className="cl-platform__orbit">
        {section.modules.map((mod, i) => (
          <span
            key={mod.id}
            className={`cl-platform__label cl-platform__label--${mod.position}`}
            style={{ animationDelay: `${i * 0.35}s` }}
          >
            <span className="cl-platform__label-dot" aria-hidden />
            {mod.label}
          </span>
        ))}
      </div>
    </div>
  </SiteSection>
);

function getConnectorEnd(mod: SolutionPlatformModuleLabel): { x: number; y: number } {
  switch (mod.position) {
    case 'top':
      return { x: 500, y: 36 };
    case 'top-left':
      return { x: 168, y: 108 };
    case 'top-right':
      return { x: 832, y: 108 };
    case 'left':
      return { x: 72, y: 310 };
    case 'right':
      return { x: 928, y: 310 };
    case 'bottom-left':
      return { x: 168, y: 512 };
    case 'bottom':
      return { x: 500, y: 584 };
    case 'bottom-right':
      return { x: 832, y: 512 };
    default:
      return { x: 500, y: 310 };
  }
}
